import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, Plus, X, ChevronDown, Edit3, Trash2, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = (url) => axios.get(`${API_URL}${url}`, { withCredentials: true }).catch(() => axios.get(`${API_URL}${url}`));

const STATUSES = ['identified', 'researching', 'applied', 'confirmed', 'attended', 'declined'];
const PRIORITY_COLORS = { red: { bg: '#DC262615', text: '#DC2626', label: '🔴 Immediate' }, orange: { bg: '#F59E0B15', text: '#D97706', label: '🟠 This Month' }, green: { bg: '#10B98115', text: '#059669', label: '🟢 Long-Term' } };

const emptyOpp = { name: '', organisation: '', industry: '', category: '', country: '', city: '', date: '', deadline: '', cost: 0, estimated_revenue: 0, relevant_businesses: [], status: 'identified', priority: 'green', contact_name: '', contact_email: '', contact_phone: '', website: '', notes: '', revenue_ideas: [], revenue_potential: 5, relationship_value: 5, brand_visibility: 5, strategic_importance: 5, time_investment: 5, probability_of_success: 5 };

const OpportunitiesPage = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editOpp, setEditOpp] = useState(null);
  const [form, setForm] = useState({ ...emptyOpp });
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    api('/api/opportunities/categories/list').then(r => {
      setCategories(r.data.categories || []);
      setBusinesses(r.data.businesses || []);
    }).catch(() => {});
    if (searchParams.get('new') === '1') { setShowForm(true); setForm({ ...emptyOpp }); }
  }, []);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (searchQuery) params.set('search', searchQuery);
      const res = await api(`/api/opportunities?${params.toString()}`);
      setOpportunities(res.data);
    } catch { setOpportunities([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filters, searchQuery]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editOpp) {
        await axios.put(`${API_URL}/api/opportunities/${editOpp.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/opportunities`, form, { withCredentials: true });
      }
      setShowForm(false); setEditOpp(null); setForm({ ...emptyOpp }); fetchData();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this opportunity?')) return;
    await axios.delete(`${API_URL}/api/opportunities/${id}`, { withCredentials: true }).catch(() => {});
    fetchData();
  };

  const openEdit = (opp) => { setEditOpp(opp); setForm({ ...emptyOpp, ...opp }); setShowForm(true); };

  const toggleBusiness = (b) => {
    setForm(prev => ({ ...prev, relevant_businesses: prev.relevant_businesses.includes(b) ? prev.relevant_businesses.filter(x => x !== b) : [...prev.relevant_businesses, b] }));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAF6', fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="opportunities-page">
      <nav className="bg-white border-b px-6 py-3" style={{ borderColor: '#E8E0D4' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 no-underline">
              <img src="/maluti-logo-web.png" alt="Maluti" className="h-7 object-contain" />
              <span className="text-xs font-medium" style={{ color: '#2C1A0E' }}>Ops Suite</span>
            </Link>
            <span className="text-xs" style={{ color: '#D1D5DB' }}>/</span>
            <Link to="/bi-dashboard" className="text-xs no-underline hover:underline" style={{ color: '#8B7355' }}>BI</Link>
            <span className="text-xs" style={{ color: '#D1D5DB' }}>/</span>
            <span className="text-xs font-semibold" style={{ color: '#B8860B' }}>Opportunities</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/bi-dashboard" className="text-[10px] uppercase tracking-wider font-medium no-underline hover:underline" style={{ color: '#8B7355' }}>Dashboard</Link>
            <Link to="/annual-planner" className="text-[10px] uppercase tracking-wider font-medium no-underline hover:underline" style={{ color: '#8B7355' }}>Planner</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header + Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
          <h1 className="text-xl font-light" style={{ color: '#2C1A0E' }}>Opportunities <span className="text-sm font-normal" style={{ color: '#8B7355' }}>({opportunities.length})</span></h1>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: '#8B7355' }} />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search opportunities..." className="pl-9 pr-3 py-2 text-xs border rounded-sm w-full sm:w-56" style={{ borderColor: '#E8E0D4' }} data-testid="search-opps" />
            </div>
            <button onClick={() => { setEditOpp(null); setForm({ ...emptyOpp }); setShowForm(true); }} className="px-4 py-2 text-[10px] uppercase tracking-wider font-medium whitespace-nowrap" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="add-opp-btn">
              <Plus className="w-3 h-3 inline mr-1" /> Add
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-5">
          {[
            { key: 'priority', label: 'Priority', options: ['red', 'orange', 'green'] },
            { key: 'status', label: 'Status', options: STATUSES },
            { key: 'category', label: 'Category', options: categories.slice(0, 10) },
            { key: 'business', label: 'Business', options: businesses },
          ].map(({ key, label, options }) => (
            <select key={key} value={filters[key] || ''} onChange={e => setFilters(p => ({ ...p, [key]: e.target.value }))} className="px-2 py-1.5 text-[10px] border rounded-sm" style={{ borderColor: '#E8E0D4', color: filters[key] ? '#2C1A0E' : '#8B7355' }} data-testid={`filter-${key}`}>
              <option value="">{label}</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          {Object.values(filters).some(Boolean) && (
            <button onClick={() => setFilters({})} className="text-[10px] underline" style={{ color: '#B8860B' }}>Clear</button>
          )}
        </div>

        {/* Opportunities List */}
        <div className="flex flex-col gap-3">
          {opportunities.map(opp => (
            <div key={opp.id} className="bg-white p-4 border rounded-sm flex flex-col sm:flex-row items-start gap-3 hover:shadow-sm transition-shadow cursor-pointer" style={{ borderColor: '#E8E0D4', borderLeftWidth: '3px', borderLeftColor: PRIORITY_COLORS[opp.priority]?.text || '#10B981' }} onClick={() => openEdit(opp)} data-testid={`opp-${opp.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-medium truncate" style={{ color: '#2C1A0E' }}>{opp.name}</h3>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: PRIORITY_COLORS[opp.priority]?.bg, color: PRIORITY_COLORS[opp.priority]?.text }}>{PRIORITY_COLORS[opp.priority]?.label}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {opp.organisation && <span className="text-[10px]" style={{ color: '#8B7355' }}>{opp.organisation}</span>}
                  {opp.category && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#F5F0E8', color: '#6B5B4E' }}>{opp.category}</span>}
                  {opp.city && <span className="text-[10px]" style={{ color: '#8B7355' }}>{opp.city}{opp.country ? `, ${opp.country}` : ''}</span>}
                  {opp.date && <span className="text-[10px]" style={{ color: '#8B7355' }}>{new Date(opp.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                </div>
                {opp.relevant_businesses?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {opp.relevant_businesses.map(b => <span key={b} className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#D4AF3715', color: '#B8860B' }}>{b}</span>)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                {opp.estimated_revenue > 0 && <span className="text-sm font-semibold" style={{ color: '#B8860B' }}>£{opp.estimated_revenue.toLocaleString()}</span>}
                {opp.deadline && <span className="text-[10px]" style={{ color: new Date(opp.deadline) < new Date() ? '#DC2626' : '#8B7355' }}>Due {new Date(opp.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>}
                <span className="text-[10px] px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: '#F5F0E8', color: '#6B5B4E' }}>{opp.status}</span>
                <button onClick={(e) => { e.stopPropagation(); handleDelete(opp.id); }} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} /></button>
              </div>
            </div>
          ))}
          {!loading && opportunities.length === 0 && (
            <div className="text-center py-16 bg-white border rounded-sm" style={{ borderColor: '#E8E0D4' }}>
              <p className="text-sm mb-2" style={{ color: '#8B7355' }}>No opportunities yet</p>
              <button onClick={() => { setEditOpp(null); setForm({ ...emptyOpp }); setShowForm(true); }} className="text-xs underline" style={{ color: '#B8860B' }}>Add your first opportunity</button>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: 'rgba(44,26,14,0.5)' }} onClick={() => { setShowForm(false); setEditOpp(null); }}>
          <form onSubmit={handleSave} onClick={e => e.stopPropagation()} className="bg-white w-full sm:max-w-2xl sm:rounded-lg rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="opp-form">
            <div className="sticky top-0 bg-white p-5 border-b flex items-center justify-between" style={{ borderColor: '#E8E0D4' }}>
              <h3 className="text-sm font-semibold" style={{ color: '#2C1A0E' }}>{editOpp ? 'Edit Opportunity' : 'New Opportunity'}</h3>
              <button type="button" onClick={() => { setShowForm(false); setEditOpp(null); }}><X className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="p-5 flex flex-col gap-4">
              {/* Core Info */}
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Opportunity name *" className="w-full px-3 py-2.5 text-sm border rounded-sm" style={{ borderColor: '#E8E0D4' }} required autoFocus data-testid="opp-name" />
              <div className="grid grid-cols-2 gap-3">
                <input value={form.organisation} onChange={e => setForm(p => ({ ...p, organisation: e.target.value }))} placeholder="Organisation" className="px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} />
                <input value={form.industry} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))} placeholder="Industry" className="px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }}>
                  <option value="">Category</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {/* Location + Dates */}
              <div className="grid grid-cols-3 gap-3">
                <input value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} placeholder="Country" className="px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} />
                <input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} placeholder="City" className="px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} />
                <input value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="Website URL" className="px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] uppercase tracking-wider mb-1 block" style={{ color: '#8B7355' }}>Event Date</label><input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} /></div>
                <div><label className="text-[10px] uppercase tracking-wider mb-1 block" style={{ color: '#8B7355' }}>Deadline</label><input type="date" value={form.deadline} onChange={e => setForm(p => ({ ...p, deadline: e.target.value }))} className="w-full px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} /></div>
              </div>
              {/* Financial */}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] uppercase tracking-wider mb-1 block" style={{ color: '#8B7355' }}>Cost (£)</label><input type="number" value={form.cost} onChange={e => setForm(p => ({ ...p, cost: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} /></div>
                <div><label className="text-[10px] uppercase tracking-wider mb-1 block" style={{ color: '#8B7355' }}>Est. Revenue (£)</label><input type="number" value={form.estimated_revenue} onChange={e => setForm(p => ({ ...p, estimated_revenue: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} /></div>
              </div>
              {/* Businesses */}
              <div>
                <label className="text-[10px] uppercase tracking-wider mb-2 block" style={{ color: '#8B7355' }}>Relevant Businesses</label>
                <div className="flex flex-wrap gap-2">
                  {businesses.map(b => (
                    <button key={b} type="button" onClick={() => toggleBusiness(b)} className="px-2 py-1 text-[10px] rounded-full border transition-all" style={{ borderColor: form.relevant_businesses.includes(b) ? '#B8860B' : '#E8E0D4', backgroundColor: form.relevant_businesses.includes(b) ? '#D4AF3720' : '#FFF', color: form.relevant_businesses.includes(b) ? '#B8860B' : '#8B7355' }}>{b}</button>
                  ))}
                </div>
              </div>
              {/* Contact */}
              <div className="grid grid-cols-3 gap-3">
                <input value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} placeholder="Contact name" className="px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} />
                <input value={form.contact_email} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))} placeholder="Contact email" className="px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} />
                <input value={form.contact_phone} onChange={e => setForm(p => ({ ...p, contact_phone: e.target.value }))} placeholder="Contact phone" className="px-3 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} />
              </div>
              {/* Scoring */}
              <div>
                <label className="text-[10px] uppercase tracking-wider mb-2 block" style={{ color: '#8B7355' }}>Opportunity Scoring (1-10)</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'revenue_potential', label: 'Revenue' },
                    { key: 'relationship_value', label: 'Relationships' },
                    { key: 'brand_visibility', label: 'Brand' },
                    { key: 'strategic_importance', label: 'Strategic' },
                    { key: 'time_investment', label: 'Time Cost' },
                    { key: 'probability_of_success', label: 'Probability' },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="text-[9px] block mb-1" style={{ color: '#8B7355' }}>{label}</label>
                      <input type="range" min="1" max="10" value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: parseInt(e.target.value) }))} className="w-full accent-amber-600" />
                      <span className="text-[10px] font-medium" style={{ color: '#B8860B' }}>{form[key]}/10</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Notes */}
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notes, context, strategy..." rows={3} className="w-full px-3 py-2 text-xs border rounded-sm resize-none" style={{ borderColor: '#E8E0D4' }} />
            </div>
            <div className="sticky bottom-0 bg-white p-5 border-t flex justify-end gap-3" style={{ borderColor: '#E8E0D4' }}>
              <button type="button" onClick={() => { setShowForm(false); setEditOpp(null); }} className="px-4 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4', color: '#8B7355' }}>Cancel</button>
              <button type="submit" disabled={saving || !form.name} className="px-6 py-2 text-xs uppercase tracking-wider font-medium rounded-sm disabled:opacity-50" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="opp-save-btn">{saving ? 'Saving...' : editOpp ? 'Update' : 'Create Opportunity'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default OpportunitiesPage;
